import { QueryAggregate, type MultiTableViewDefinition } from '@powerportalspro/core';
import type { ViewDefinition } from '@powerportalspro/react-fluent';

// The records the detail page demos feature, and the views the detail pages list related records
// in. Each demo shows one featured record from the shared sample data, so the page is the same for
// everybody who opens it; the account, contact and opportunity pages the grids open show any.

/** Schmeler and Sons: a logo, six contacts, won, lost and open deals, and documents. */
export const ACCOUNT_ID = '1e978f59-ee34-f111-88b4-6045bd02c77d';

/** One of that account's open deals. */
export const OPPORTUNITY_ID = 'e506f9c8-2b3b-f111-bec5-6045bd05e891';

export const OPEN_OPPORTUNITIES_VIEW_ID = '2b4d6f8a-0c1e-4a3b-9d5f-7e9a1c3b5d70';
export const CLOSED_OPPORTUNITIES_VIEW_ID = '6e8a0c2b-4d6f-4b1a-8c3e-5a7c9e1b3d82';
export const OPPORTUNITIES_VIEW_ID = '5d0c7a3e-91b4-4f6a-8e2d-3b7c9a1f4e60';
export const ACCOUNT_DOCUMENTS_VIEW_ID = '8a2f4c6e-1b3d-4e5f-9a7c-0d2e4f6a8b1c';
export const CONTACT_DOCUMENTS_VIEW_ID = 'd2c4bc58-8247-441f-ab3d-9e57adcbd3a8';
export const ACCOUNT_CONTACTS_VIEW_ID = 'c3e5a7b9-2d4f-4a6c-8e1b-5f7a9c3e5d2b';

// The opportunities the figures across the top of the account and contact pages are worked out
// over. Each card keeps them to the record through its relationship, so they filter only by status.

/** Open deals. */
export const OPEN_OPPORTUNITIES_FETCH_XML =
  '<fetch><entity name="opportunity"><filter><condition attribute="statecode" operator="eq" value="0" /></filter></entity></fetch>';

/** Won deals. */
export const WON_OPPORTUNITIES_FETCH_XML =
  '<fetch><entity name="opportunity"><filter><condition attribute="statecode" operator="eq" value="1" /></filter></entity></fetch>';

/** Won and lost deals: those a win rate is a share of. */
export const CLOSED_OPPORTUNITIES_FETCH_XML =
  '<fetch><entity name="opportunity"><filter><condition attribute="statecode" operator="in"><value>1</value><value>2</value></condition></filter></entity></fetch>';

// An account's or a contact's opportunities, in three views. The SubGrid scopes each to the record
// through its relationship, so the views filter only by status.

/** The open deals, closing soonest first, with their forecast totaled. */
export function getOpenOpportunitiesView(displayName: string): ViewDefinition {
  return {
    id: OPEN_OPPORTUNITIES_VIEW_ID,
    tableName: 'opportunity',
    displayName,
    fetchXml: `<fetch>
      <entity name="opportunity">
        <attribute name="name" />
        <attribute name="estimatedvalue" />
        <attribute name="closeprobability" />
        <attribute name="estimatedclosedate" />
        <attribute name="opportunityratingcode" />
        <order attribute="estimatedclosedate" />
        <filter>
          <condition attribute="statecode" operator="eq" value="0" />
        </filter>
      </entity>
    </fetch>`,
    columns: [
      { columnName: 'name', width: 300, aggregates: [{ aggregate: QueryAggregate.Count }] },
      { columnName: 'estimatedvalue', width: 160, aggregates: [{ aggregate: QueryAggregate.Sum, format: 'C0' }] },
      { columnName: 'closeprobability', width: 130, aggregates: [{ aggregate: QueryAggregate.Average, format: 'N0' }] },
      { columnName: 'estimatedclosedate', width: 150 },
      { columnName: 'opportunityratingcode', width: 110 },
    ],
  };
}

/** The won and lost deals, latest first, with what they closed at totaled. */
export function getClosedOpportunitiesView(displayName: string): ViewDefinition {
  return {
    id: CLOSED_OPPORTUNITIES_VIEW_ID,
    tableName: 'opportunity',
    displayName,
    fetchXml: `<fetch>
      <entity name="opportunity">
        <attribute name="name" />
        <attribute name="statecode" />
        <attribute name="actualvalue" />
        <attribute name="actualclosedate" />
        <order attribute="actualclosedate" descending="true" />
        <filter>
          <condition attribute="statecode" operator="in">
            <value>1</value>
            <value>2</value>
          </condition>
        </filter>
      </entity>
    </fetch>`,
    columns: [
      { columnName: 'name', width: 300, aggregates: [{ aggregate: QueryAggregate.Count }] },
      { columnName: 'statecode', width: 110 },
      { columnName: 'actualvalue', width: 160, aggregates: [{ aggregate: QueryAggregate.Sum, format: 'C0' }] },
      { columnName: 'actualclosedate', width: 150 },
    ],
  };
}

/** All the deals grouped by status, each group counted and totaled. */
export function getOpportunitiesView(displayName: string): ViewDefinition {
  return {
    id: OPPORTUNITIES_VIEW_ID,
    tableName: 'opportunity',
    displayName,
    fetchXml: `<fetch>
      <entity name="opportunity">
        <attribute name="name" />
        <attribute name="estimatedvalue" />
        <attribute name="closeprobability" />
        <attribute name="estimatedclosedate" />
        <attribute name="statecode" />
        <order attribute="estimatedclosedate" descending="true" />
      </entity>
    </fetch>`,
    groupLevels: [{ columnName: 'statecode' }],
    columns: [
      { columnName: 'name', width: 300, aggregates: [{ aggregate: QueryAggregate.Count }] },
      { columnName: 'estimatedvalue', width: 160, aggregates: [{ aggregate: QueryAggregate.Sum, format: 'C0' }] },
      { columnName: 'closeprobability', width: 130, aggregates: [{ aggregate: QueryAggregate.Average, format: 'N0' }] },
      { columnName: 'estimatedclosedate', width: 150 },
      { columnName: 'statecode', width: 110 },
    ],
  };
}

/** The files linked to one account, newest first. */
export function getAccountDocumentsView(accountId: string, displayName: string): ViewDefinition {
  return getDocumentsView(ACCOUNT_DOCUMENTS_VIEW_ID, 'ppp_accountid', accountId, displayName);
}

/** The files linked to one contact, newest first. */
export function getContactDocumentsView(contactId: string, displayName: string): ViewDefinition {
  return getDocumentsView(CONTACT_DOCUMENTS_VIEW_ID, 'ppp_contactid', contactId, displayName);
}

// The ppp_file rows whose lookup in that column points at one record.
function getDocumentsView(viewId: string, lookupColumn: string, recordId: string, displayName: string): ViewDefinition {
  return {
    id: viewId,
    tableName: 'ppp_file',
    displayName,
    fetchXml: `<fetch>
      <entity name="ppp_file">
        <attribute name="ppp_filename" />
        <attribute name="ppp_data" />
        <attribute name="createdon" />
        <order attribute="createdon" descending="true" />
        <filter>
          <condition attribute="${lookupColumn}" operator="eq" value="${recordId}" />
        </filter>
      </entity>
    </fetch>`,
    columns: [
      { columnName: 'ppp_filename', width: 320 },
      { columnName: 'createdon', width: 180 },
    ],
  };
}

/** The contacts who work for one account, by name. */
export function getAccountContactsView(accountId: string, displayName: string): ViewDefinition {
  return {
    id: ACCOUNT_CONTACTS_VIEW_ID,
    tableName: 'contact',
    displayName,
    fetchXml: `<fetch>
      <entity name="contact">
        <attribute name="fullname" />
        <attribute name="jobtitle" />
        <attribute name="emailaddress1" />
        <attribute name="telephone1" />
        <attribute name="mobilephone" />
        <order attribute="fullname" />
        <filter>
          <condition attribute="parentcustomerid" operator="eq" value="${accountId}" />
        </filter>
      </entity>
    </fetch>`,
    columns: [
      { columnName: 'fullname', width: 200 },
      { columnName: 'jobtitle', width: 220 },
      { columnName: 'emailaddress1', width: 240 },
      { columnName: 'telephone1', width: 150 },
      { columnName: 'mobilephone', width: 150 },
    ],
  };
}

const openOnly = (table: string) =>
  `<fetch><entity name="${table}"><filter><condition attribute="statecode" operator="eq" value="0" /></filter></entity></fetch>`;

/**
 * The calendar's views of one record's activities, its `regarding` deciding whose: its meetings,
 * tasks and phone calls together, and what is still to do — the tasks and calls not yet done.
 */
export function getCalendarViews(activitiesName: string, toDoName: string): MultiTableViewDefinition[] {
  return [
    {
      id: '3e8b0f25-6c4d-4a9b-8f72-1e5c8d3b9a21',
      displayName: activitiesName,
      tables: [{ table: 'appointment' }, { table: 'task' }, { table: 'phonecall' }],
    },
    {
      id: '3e8b0f25-6c4d-4a9b-8f72-1e5c8d3b9a22',
      displayName: toDoName,
      tables: [
        { table: 'task', fetchXml: openOnly('task') },
        { table: 'phonecall', fetchXml: openOnly('phonecall') },
      ],
    },
  ];
}
